import { apiErrorMessage } from '@/api/client';
import {
  GHOST_BUTTON,
  INPUT,
  LARGE_MODAL,
  MenuCheck,
  normalizeText,
  primaryClasses,
  segClasses,
  typeBadgeStyle,
} from '@/components/admin/ui';
import { Modal } from '@/components/common/Modal';
import { SearchIcon } from '@/components/common/icons';
import { ITEM_TYPES, type CategoryModel, type ItemModel, type ItemType } from '@/types/categories.types';
import type { FC } from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface AttachItemsModalProps {
  category: CategoryModel;
  categories: CategoryModel[];
  items: ItemModel[];
  onAttach: (itemIds: string[]) => Promise<void>;
  onClose: () => void;
}

/** The "from category" filter value for items in no category. */
const NO_CATEGORY = '__none';

/**
 * The prototype's "Přidat položky do kategorie …": every item not yet in the category, filtered by
 * text, type and the category it comes from, picked with checkboxes and attached in one request
 * (`POST /categories/{key}/items`).
 */
export const AttachItemsModal: FC<AttachItemsModalProps> = ({ category, categories, items, onAttach, onClose }) => {
  const { t } = useTranslation();
  const titleId = useId();
  const [query, setQuery] = useState('');
  const [types, setTypes] = useState<ItemType[]>([]);
  const [fromCategory, setFromCategory] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const q = normalizeText(query.trim());
  const labelOf = (key: string) => categories.find((entry) => entry.key === key)?.label_cs;
  const candidates = items.filter(
    (item) =>
      !item.categories.includes(category.key) &&
      (!q || normalizeText(item.name).includes(q) || normalizeText(item.id).includes(q)) &&
      (!types.length || types.includes(item.type)) &&
      (!fromCategory ||
        (fromCategory === NO_CATEGORY ? item.categories.length === 0 : item.categories.includes(fromCategory)))
  );

  const toggle = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];

  const save = async () => {
    if (!selected.length) return;
    setIsSaving(true);
    setError(null);
    try {
      await onAttach(selected);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, t('admin.saveFailed')));
      setIsSaving(false);
    }
  };

  return (
    <Modal onClose={onClose} maxWidth={920} labelledBy={titleId} className={LARGE_MODAL}>
      <div className="flex flex-col gap-3 border-b border-line px-[22px] pb-3.5 pt-[18px]">
        <div className="flex items-center gap-[9px]">
          <span className="inline-block h-2.5 w-2.5 flex-none" style={{ background: category.color }} />
          <div id={titleId} className="text-base font-bold text-primary">
            {t('admin.attachDialog.title', { category: category.label_cs })}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex flex-[1_1_240px] items-center">
            <SearchIcon
              size={13}
              strokeWidth={2.2}
              stroke="#6d7f91"
              className="pointer-events-none absolute left-[9px]"
            />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={t('admin.attachDialog.search')}
              aria-label={t('admin.attachDialog.search')}
              // biome-ignore lint/a11y/noAutofocus: the dialog starts with the search
              autoFocus={true}
              className={`${INPUT} w-full py-[7px] pl-7 pr-2.5 text-xs`}
            />
          </div>
          <div className="flex overflow-hidden rounded border border-control">
            {ITEM_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                aria-pressed={types.includes(type)}
                onClick={() => setTypes(toggle(types, type))}
                className={segClasses(types.includes(type))}
              >
                {t(`admin.types.${type}`)}
              </button>
            ))}
          </div>
          <select
            value={fromCategory}
            onChange={(event) => setFromCategory(event.target.value)}
            aria-label={t('admin.attachDialog.anyCategory')}
            className={`${INPUT} max-w-[220px] cursor-pointer px-2 py-[7px] text-xs`}
          >
            <option value="">{t('admin.attachDialog.anyCategory')}</option>
            {categories
              .filter((entry) => entry.key !== category.key)
              .map((entry) => (
                <option key={entry.key} value={entry.key}>
                  {entry.label_cs}
                </option>
              ))}
            <option value={NO_CATEGORY}>{t('admin.attachDialog.noCategory')}</option>
          </select>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {candidates.map((item) => {
          const on = selected.includes(item.id);
          const cats = item.categories.map(labelOf).filter(Boolean).join(', ');
          return (
            <button
              key={item.id}
              type="button"
              role="checkbox"
              aria-checked={on}
              onClick={() => setSelected(toggle(selected, item.id))}
              className={`flex w-full cursor-pointer items-center gap-3.5 border-x-0 border-b border-t-0 border-hairline px-[22px] py-2.5 text-left font-[inherit] hover:bg-[#f2f7fb] ${
                on ? 'bg-[#f2f7fb]' : 'bg-white'
              }`}
            >
              <MenuCheck on={on} />
              <span className="flex min-w-0 flex-[0_0_200px] flex-col gap-0.5">
                <span className="text-[12.5px] font-semibold text-ink">{item.name}</span>
                <span className="font-mono text-[10.5px] text-label-2">{item.id}</span>
              </span>
              <span
                className="w-[52px] flex-none px-1.5 py-0.5 text-center text-[10.5px] font-semibold"
                style={typeBadgeStyle(item.type)}
              >
                {t(`admin.types.${item.type}`)}
              </span>
              <span className="min-w-0 flex-1 truncate text-[11.5px] text-muted">
                {cats || t('admin.attachDialog.noCategory')}
              </span>
            </button>
          );
        })}
        {candidates.length === 0 ? (
          <div className="px-[22px] py-7 text-[12.5px] text-label-2">{t('admin.attachDialog.empty')}</div>
        ) : null}
      </div>

      <div className="flex items-center gap-2 border-t border-hairline px-[22px] pb-[18px] pt-3">
        <span className={`flex-1 text-[11.5px] ${error ? 'text-danger' : 'text-label-2'}`} aria-live="polite">
          {error ??
            (selected.length
              ? t('admin.attachDialog.selected', { count: selected.length })
              : t('admin.attachDialog.available', { count: candidates.length }))}
        </span>
        <button type="button" onClick={onClose} className={GHOST_BUTTON}>
          {t('admin.attachDialog.cancel')}
        </button>
        <button
          type="button"
          onClick={save}
          disabled={!selected.length || isSaving}
          className={primaryClasses(selected.length > 0)}
        >
          {selected.length
            ? t('admin.attachDialog.submitCount', { count: selected.length })
            : t('admin.attachDialog.submit')}
        </button>
      </div>
    </Modal>
  );
};
